import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  ReactNode,
} from "react";

import { DEFAULT_CONFIG } from "./spacemouse.constants";
import {
  SpaceMouseConfig,
  SpaceMouseConnectionStatus,
  SpaceMouseDeviceInfo,
} from "./spacemouse.types";
import {
  SpaceMouseService,
  createSpaceMouseService,
} from "./SpaceMouseService";

interface SpaceMouseContextValue {
  status: SpaceMouseConnectionStatus;
  isSupported: boolean;
  deviceInfo: SpaceMouseDeviceInfo | null;
  connect: () => Promise<boolean>;
  disconnect: () => Promise<void>;
  setConfig: (config: Partial<SpaceMouseConfig>) => void;
  config: SpaceMouseConfig;
}

const SpaceMouseContext = createContext<SpaceMouseContextValue | null>(null);

interface SpaceMouseProviderProps {
  children: ReactNode;
  initialConfig?: Partial<SpaceMouseConfig>;
  onPan?: (deltaX: number, deltaY: number) => void;
  onZoom?: (direction: number) => void;
  onRotateLeft?: () => void;
  onRotateRight?: () => void;
}

export function SpaceMouseProvider({
  children,
  initialConfig,
  onPan,
  onZoom,
  onRotateLeft,
  onRotateRight,
}: SpaceMouseProviderProps) {
  const [status, setStatus] = useState<SpaceMouseConnectionStatus>(
    SpaceMouseService.isSupported() ? "disconnected" : "unsupported"
  );
  const [config, setConfigState] = useState<SpaceMouseConfig>({
    ...DEFAULT_CONFIG,
    ...initialConfig,
  });
  const [deviceInfo, setDeviceInfo] = useState<SpaceMouseDeviceInfo | null>(
    null
  );

  const serviceRef = useRef<SpaceMouseService | null>(null);

  const callbacksRef = useRef({ onPan, onZoom, onRotateLeft, onRotateRight });

  useEffect(() => {
    callbacksRef.current = { onPan, onZoom, onRotateLeft, onRotateRight };
  }, [onPan, onZoom, onRotateLeft, onRotateRight]);

  useEffect(() => {
    serviceRef.current = createSpaceMouseService(config, {
      onPan: (deltaX, deltaY) => {
        callbacksRef.current.onPan?.(deltaX, deltaY);
      },
      onZoom: (direction) => {
        callbacksRef.current.onZoom?.(direction);
      },
      onButtonPress: (button) => {
        if (button === 1) {
          callbacksRef.current.onRotateLeft?.();
        } else if (button === 2) {
          callbacksRef.current.onRotateRight?.();
        }
      },
      onConnect: () => {
        setStatus("connected");
        setDeviceInfo(serviceRef.current?.getDeviceInfo() || null);
      },
      onDisconnect: () => {
        setStatus("disconnected");
        setDeviceInfo(null);
      },
    });

    serviceRef.current.tryAutoConnect();

    return () => {
      serviceRef.current?.disconnect();
    };
  }, []);

  const connect = useCallback(async () => {
    if (!serviceRef.current) return false;

    setStatus("connecting");

    const success = await serviceRef.current.connect();

    if (!success) {
      const serviceStatus =
        serviceRef.current && "status" in serviceRef.current
          ? (serviceRef.current as any).status
          : undefined;
      if (serviceStatus) {
        setStatus(serviceStatus as SpaceMouseConnectionStatus);
      } else {
        setStatus("disconnected");
      }
    }

    return success;
  }, []);

  const disconnect = useCallback(async () => {
    if (serviceRef.current) {
      await serviceRef.current.disconnect();
    }
  }, []);

  const setConfig = useCallback((newConfig: Partial<SpaceMouseConfig>) => {
    setConfigState((prev) => {
      const updated = { ...prev, ...newConfig };
      serviceRef.current?.setConfig(updated);
      return updated;
    });
  }, []);

  const value: SpaceMouseContextValue = {
    status,
    isSupported: SpaceMouseService.isSupported(),
    deviceInfo,
    connect,
    disconnect,
    setConfig,
    config,
  };

  return (
    <SpaceMouseContext.Provider value={value}>
      {children}
    </SpaceMouseContext.Provider>
  );
}

export function useSpaceMouseContext(): SpaceMouseContextValue {
  const context = useContext(SpaceMouseContext);
  if (!context) {
    throw new Error(
      "useSpaceMouseContext must be used within SpaceMouseProvider"
    );
  }
  return context;
}
